import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  accordionSummaryClasses,
  Box,
  SxProps,
  useTheme,
} from '@mui/material';

import { AppTypography } from './app-typography';
import { StackColumn, StackRow, StackRowForm } from './stack-column';
import { IconChevronDown } from 'src/libraries/icons';

//
//

export type AppAccordionProps = {
  children: React.ReactNode;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  explainer?: React.ReactNode;
  expanded?: boolean;
  defaultExpanded?: boolean;
  onChange?: (expanded: boolean) => void;
  border?: boolean;
  actions?: React.ReactNode;
  sx?: SxProps;
  titleColor?: string;
  forceCollapsed?: boolean;
  collapsible?: boolean;
};

export const AppAccordion: React.FC<AppAccordionProps> = ({
  children,
  title,
  subtitle,
  explainer,
  expanded: expandedProp,
  defaultExpanded = false,
  onChange,
  border = true,
  actions,
  sx,
  titleColor,
  forceCollapsed = false,
  collapsible = true,
}) => {
  const theme = useTheme();
  const [expandedState, setExpandedState] = useState(defaultExpanded);
  const isControlled = expandedProp !== undefined;
  const expanded = isControlled ? expandedProp : expandedState;

  useEffect(() => {
    if (forceCollapsed) setExpandedState(false);
  }, [forceCollapsed]);

  const handleChange = () => {
    if (!collapsible) return;
    if (!isControlled) setExpandedState(!expanded);
    onChange?.(!expanded);
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={handleChange}
      disableGutters
      elevation={0}
      square
      sx={{
        '&::before': { display: 'none' },
        border: (theme) => (border ? `1px solid ${theme.palette.divider}` : 'none'),
        background: (theme) => theme.palette.background.paper,
        ...sx,
      }}
    >
      <AccordionSummary
        expandIcon={collapsible ? <IconChevronDown size={16} color={theme.palette.text.secondary} /> : undefined}
        sx={{
          [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]: {
            transform: 'rotate(180deg)',
          },
          cursor: collapsible ? 'pointer' : 'default',
          px: 2,
          minHeight: 62
        }}
      >
        <StackRow justifyContent="space-between" flex={1}>
          <StackColumn gap={0.5}>
            <StackRowForm alignItems="baseline">
              {title && <AppTypography fontWeight="bold" color={titleColor}>{title}</AppTypography>}
              {explainer && (
                <AppTypography variant="body2" color="text.secondary">
                  {explainer}
                </AppTypography>
              )}
            </StackRowForm>
            {subtitle && (
              <AppTypography variant="body2" color="text.secondary">
                {subtitle}
              </AppTypography>
            )}
          </StackColumn>

          {actions && <Box onClick={(e) => e.stopPropagation()}>{actions}</Box>}
        </StackRow>
      </AccordionSummary>

      <AccordionDetails sx={{ py: 2 }}>{children}</AccordionDetails>
    </Accordion>
  );
};
